import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import FAQSection from "@/components/FAQSection";
import Marquee from "@/components/Marquee";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Arrow120 } from "@/components/Arrow120";

const Segment = ({ segment, items }) => (
  <section className="py-20 md:py-32" data-testid={`creatives-segment-${segment.key}`}>
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 items-end">
        <div className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mb-3">{segment.title}</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-ahm-teal">
            {segment.title.split(" ").map((w, i, arr) =>
              i === arr.length - 1 ? <em key={i} className="not-italic text-ahm-orange">{w}</em> : <span key={i}>{w} </span>
            )}
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-base md:text-lg text-ahm-teal/80 leading-relaxed">{segment.blurb}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.slice(0, 6).map((it, i) => (
          <motion.div
            key={it.id || i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
            className="group relative overflow-hidden rounded-2xl bg-ahm-muted aspect-[4/5]"
          >
            <Link to={`/creatives/${segment.key}`} data-testid={`creatives-item-${segment.key}-${i}`}>
              <img src={it.image} alt={it.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-ahm-teal/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5 text-ahm-cream">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ahm-yellow">{String(i + 1).padStart(2, "0")}</p>
                <h4 className="font-display text-2xl leading-tight mt-1">{it.title}</h4>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
      <div className="mt-12 flex">
        <Link to={`/creatives/${segment.key}`} className="btn-pill bg-ahm-teal hover:bg-ahm-orange group" data-testid={`creatives-see-more-${segment.key}`}>
          See more
          <Arrow120 />
        </Link>
      </div>
    </div>
  </section>
);

const Creatives = () => {
  const [segments, setSegments] = useState([]);
  const [itemsByCat, setItems] = useState({});
  const [logos, setLogos] = useState([]);

  useEffect(() => {
    api.get("/creatives_segments").then((r) => setSegments(r.data));
    api.get("/creatives_items").then((r) => {
      const grouped = {};
      r.data.forEach((it) => {
        grouped[it.category] = grouped[it.category] || [];
        grouped[it.category].push(it);
      });
      setItems(grouped);
    });
    api.get("/client_logos").then((r) => setLogos(r.data));
  }, []);

  return (
    <div data-testid="creatives-page">
      <section className="pt-28 pb-12 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs uppercase tracking-[0.3em] text-ahm-orange font-semibold mb-6">Our craft</p>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-ahm-teal text-balance">
            Three crafts. <em className="not-italic text-ahm-orange">One obsession.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ahm-teal/80 leading-relaxed">
            Social. Film. Paid. The three pillars of what we do — explored honestly, case by case.
          </p>
        </div>
      </section>
      {segments.map((seg) => (
        <Segment key={seg.id} segment={seg} items={itemsByCat[seg.key] || []} />
      ))}
      <FAQSection page="creatives" />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold text-center mb-6">
            We've made things for
          </p>
        </div>
        <Marquee logos={logos} />
      </section>
    </div>
  );
};

export default Creatives;
